import React from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import useBackdropClose from "../../hooks/useBackdropClose";
import { MOOD_ICONS } from "../../config/moodIcons";
import {
  Overlay, Modal, Header, Heading, Title, Period, CloseButton,
  Question, QuestionLabel, Answer, MoodAnswer, MoodIcon,
  Stats, Stat, StatText, StatValue, StatTotal, StatLabel,
} from "./styles";

const fmtDate = (ymd) => ymd.split("-").reverse().join("/");

// 1 h 08 min / 44 min. Los minutos van con dos dígitos solo si hay horas.
const fmtActiveTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (!hours) return `${minutes} min`;
  return `${hours} h ${String(minutes % 60).padStart(2, "0")} min`;
};

const IconClock = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9.5" />
    <polyline points="12 6.5 12 12 15.5 14" />
  </svg>
);

const IconCalendar = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3.5" y="5" width="17" height="16" rx="2" />
    <line x1="8" y1="3" x2="8" y2="7" />
    <line x1="16" y1="3" x2="16" y2="7" />
    <line x1="3.5" y1="10.5" x2="20.5" y2="10.5" />
  </svg>
);

/**
 * Resultados de una semana ya terminada del Roadtrip del paciente: sus respuestas
 * del Weekly Form, si lo completó, y el tiempo y los días activos.
 */
const RoadtripWeekModal = ({ week, onClose }) => {
  const { t } = useTranslation();
  const { form } = week;

  return createPortal(
    <Overlay {...useBackdropClose(onClose)}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Heading>
            <Title>{t("summary.week", { number: week.week_number })}</Title>
            <Period>
              {t("weeklyForm.period", { start: fmtDate(week.start_date), end: fmtDate(week.end_date) })}
            </Period>
          </Heading>
          <CloseButton type="button" onClick={onClose} aria-label={t("summary.close")}>×</CloseButton>
        </Header>

        {form ? (
          <>
            <Question>
              <QuestionLabel>{t("roadtrip.weeklyPractice")}</QuestionLabel>
              <Answer>{t(`weeklyForm.options.activity_frequency.${form.activity_frequency}`)}</Answer>
            </Question>

            <Question>
              <QuestionLabel>{t("roadtrip.performance")}</QuestionLabel>
              <Answer>{t(`weeklyForm.options.performance.${form.performance}`)}</Answer>
            </Question>

            <Question>
              <QuestionLabel>{t("roadtrip.mood")}</QuestionLabel>
              <MoodAnswer as="div">
                <MoodIcon src={MOOD_ICONS[form.mood]} alt="" />
                {t(`weeklyForm.options.mood.${form.mood}`)}
              </MoodAnswer>
            </Question>
          </>
        ) : (
          <Answer>{t("roadtrip.formNotCompleted")}</Answer>
        )}

        <Stats>
          <Stat>
            <IconClock />
            <StatText>
              <StatValue>{fmtActiveTime(week.active_seconds)}</StatValue>
              <StatLabel>{t("roadtrip.activeTime")}</StatLabel>
            </StatText>
          </Stat>
          <Stat>
            <IconCalendar />
            <StatText>
              <StatValue>{week.active_days} <StatTotal>/ 7</StatTotal></StatValue>
              <StatLabel>{t("roadtrip.activeDays")}</StatLabel>
            </StatText>
          </Stat>
        </Stats>
      </Modal>
    </Overlay>,
    document.body
  );
};

export default RoadtripWeekModal;
